import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import api from '../lib/api';
import { useRefreshTrigger } from '../context/DataContext';
import PaymentIframeModal from './PaymentIframeModal';

// System-wide sentinel for an open-ended (no month limit) standing order.
// recurringChargeJob treats installmentTotal >= 999 as unlimited. Mirrors
// RECURRING_UNLIMITED in PublicCampaign.tsx.
const RECURRING_UNLIMITED = 999;

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  credit_card: 'כרטיס אשראי',
  standing_order: 'כרטיס אשראי (הו״ק)',
  bank_transfer: 'העברה בנקאית',
  check: "צ'ק",
  cash: 'מזומן',
  bit: 'ביט',
  masav: 'מס״ב — ניכוי ישיר',
  other: 'אחר',
};

const PAYMENT_METHOD_OPTIONS: [string, string][] = [
  ['credit_card', 'כרטיס אשראי'],
  ['bank_transfer', 'העברה בנקאית'],
  ['check', "צ'ק"],
  ['cash', 'מזומן'],
  ['bit', 'ביט'],
  ['masav', 'מס״ב — ניכוי ישיר'],
  ['other', 'אחר'],
];

const deriveStatus = (method: string): string => {
  if (method === 'cash' || method === 'bit' || method === 'other') return 'completed';
  if (method === 'bank_transfer') return 'completed';
  // מס״ב is deliberately absent: choosing it never creates a Donation from this
  // dialog. It opens a הרשאת מס״ב (mandate) instead, and the income row is born
  // only once the generated file is marked as transmitted to מס״ב.
  if (method === 'check') return 'pending';
  return 'completed';
};

// The date picker yields a date-only string (YYYY-MM-DD). Posting that as-is
// makes the backend store UTC midnight, which renders as a constant 03:00 in
// Israel time. Combine the picked calendar date with the current local
// time-of-day (built in the browser's timezone) so the donation carries a real
// timestamp instead of a fixed 03:00.
const buildDonationTimestamp = (dateStr: string): string => {
  const now = new Date();
  const [y, m, d] = (dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return now.toISOString();
  return new Date(
    y, m - 1, d,
    now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds(),
  ).toISOString();
};

interface Contact {
  _id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  idNumber?: string;
}

// A tuition family as returned by GET /tuition/families (bare array). Only the
// fields the picker renders are typed here.
interface TuitionFamily {
  _id: string;
  displayName: string;
  payers?: Array<{ contactId: string | null; name: string; phone: string }>;
  studentNames?: string[];
}

interface NewDonationDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export default function NewDonationDialog({ open, onClose, onSaved }: NewDonationDialogProps) {
  const triggerRefresh = useRefreshTrigger();
  // תאריך היום לפי שעון ישראל. toISOString() עובד ב-UTC, ולכן בין 00:00–03:00
  // שעון ישראל הוא היה מחזיר את האתמול ושומר את התרומה ביום הקודם.
  const today = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    nationalId: '',
  });

  // Contact-picker combobox state. Contacts are fetched server-side with
  // pagination — the org may have millions of records, so we never load them
  // all. We keep the currently-fetched page in `contactResults`, the selected
  // contact separately so the picker can display it even when the search
  // changes, and the total count header to show "refine search" hints.
  const [contactSearch, setContactSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const CONTACT_PAGE_SIZE = 30;
  const [contactResults, setContactResults] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const [orgReceiptMessage, setOrgReceiptMessage] = useState('');
  const [chargeSection46Enabled, setChargeSection46Enabled] = useState(true);
  const [chargeReceiptMessage, setChargeReceiptMessage] = useState('');
  const [defaultCampaignId, setDefaultCampaignId] = useState('');
  const [campaigns, setCampaigns] = useState<Array<{ _id: string; name: string; status?: string; ambassadors?: Array<{ slug: string; name: string }> }>>([]);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignPickerOpen, setCampaignPickerOpen] = useState(false);
  const campaignPickerRef = useRef<HTMLDivElement>(null);
  const [ambassadorSearch, setAmbassadorSearch] = useState('');
  const [ambassadorPickerOpen, setAmbassadorPickerOpen] = useState(false);
  const ambassadorPickerRef = useRef<HTMLDivElement>(null);

  // שיוך ההכנסה: לקמפיין (ברירת מחדל) או לשכר לימוד. במצב שכר לימוד נשלח
  // tuitionFamilyId, השרת רושם את ההכנסה על המשפחה, זוקף אותה לחיובים הפתוחים
  // ומחזיר tuitionApplied — ובוחר לבדו את קמפיין ברירת המחדל להנהלת החשבונות.
  const [associationMode, setAssociationMode] = useState<'campaign' | 'tuition'>('campaign');
  const [familySearch, setFamilySearch] = useState('');
  const [familyPickerOpen, setFamilyPickerOpen] = useState(false);
  const familyPickerRef = useRef<HTMLDivElement>(null);
  const [familyResults, setFamilyResults] = useState<TuitionFamily[]>([]);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<TuitionFamily | null>(null);

  const [chargeLoading, setChargeLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [iframeOpen, setIframeOpen] = useState(false);
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);

  const [donationSuccessData, setDonationSuccessData] = useState<null | {
    id: string;
    amount: number;
    donorName: string;
    paymentMethod: string;
    receiptNumber?: string;
    donorPhone?: string;
    // Set for a verify-only future-dated standing order: no charge ran, so the
    // success card shows "הוראת קבע הוקמה" + the first-charge date instead of a
    // receipt.
    futureStandingOrder?: { startDate: string };
    // Set for the מס״ב path: no Donation exists yet — only a mandate waiting on
    // the מס״ב screen. The card explains the lifecycle instead of offering a
    // receipt.
    masavMandate?: { asmachta: string; billingDay: number; installmentTotal: number };
    // Set only when the income was linked to a tuition family. `applied` is the
    // amount the server actually zeroed off the family's open charges — 0 is a
    // legitimate outcome (nothing open), so it's reported rather than hidden.
    tuition?: { familyName: string; applied: number };
  }>(null);
  const [receiptResult, setReceiptResult] = useState<null | { ok: boolean; message?: string; error?: string }>(null);
  const [receiptGenerating, setReceiptGenerating] = useState(false);
  const successAutoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Receipt handling for non-credit-card donations:
  //   'none'   — don't issue any receipt now
  //   'issue'  — auto-issue via HyPay/Tranzila terminal
  //   'upload' — attach a PDF/image the admin already has
  const [receiptMode, setReceiptMode] = useState<'none' | 'issue' | 'upload'>('none');
  // Document kind to issue. Fixed to receipt_donation — the document-type
  // picker was removed from this dialog; a campaign is always selected here, so
  // the backend resolves the actual kind from campaign.receiptType.
  const [receiptKind] = useState<'receipt' | 'receipt_donation'>('receipt_donation');
  const [terminalOptions, setTerminalOptions] = useState<Array<{ _id: string; name: string; provider: string; isDefault?: boolean }>>([]);
  const [receiptTerminalId, setReceiptTerminalId] = useState<string>('');
  // Terminal used for the credit-card iframe charge. Saved on PaymentSession so
  // /payments/success → getToken runs against the same masof. Defaults to the
  // org's default terminal but admin can override per-charge.
  const [chargeTerminalId, setChargeTerminalId] = useState<string>('');
  // Future-dated standing order (Tranzila only): verify the card now via a
  // tranmode=N (J2) transaction that captures the token WITHOUT charging, and
  // run the first real charge on `verifyStartDate`. Only offered for recurring
  // (הוראת קבע) charges on a Tranzila terminal.
  const [verifyOnly, setVerifyOnly] = useState(false);
  const [verifyStartDate, setVerifyStartDate] = useState('');
  const [uploadedReceiptFile, setUploadedReceiptFile] = useState<File | null>(null);
  const [uploadedReceiptNumber, setUploadedReceiptNumber] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/terminals');
        if (cancelled) return;
        const list = (res.data || []).filter(
          (t: any) =>
            t.isActive !== false &&
            t.type === 'organization' &&
            (t.provider === 'hyp' || t.provider === 'tranzila')
        );
        setTerminalOptions(list);
        const def = list.find((t: any) => t.isDefault) || list[0];
        if (def) {
          setReceiptTerminalId(def._id);
          setChargeTerminalId(def._id);
          // Default to issuing a real receipt through the org's terminal, so a
          // manual (non-card) donation produces an actual קבלה instead of just a
          // placeholder number. Only when a usable terminal exists; the admin can
          // still switch to "no receipt" / "upload" before saving.
          setReceiptMode('issue');
        }
      } catch (err) {
        console.error('[NewDonationDialog] failed to load terminals', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const [form, setForm] = useState({
    contactId: '',
    amount: 0,
    paymentMethod: 'credit_card' as 'credit_card' | 'bank_transfer' | 'cash' | 'bit' | 'check' | 'masav' | 'other',
    isRecurring: false,
    bankName: '',
    bankBranch: '',
    bankAccount: '',
    transferDate: today,
    transferReference: '',
    masavBankCode: '',
    masavBranchCode: '',
    masavAccountNumber: '',
    masavAccountName: '',
    masavIdNumber: '',
    // Day of month (1–28) the mandate is charged. Mandates are grouped by this
    // day when a מס״ב file is produced.
    masavBillingDay: 15,
    masavInstitutionId: '',
    masavIdType: 'individual' as 'individual' | 'company',
    // מס״ב mandate terms: a standing order (masavMonths charges, or
    // RECURRING_UNLIMITED = עד ביטול) vs. a single debit.
    masavIsRecurring: true,
    masavMonths: 12,
    holderIdNumber: '',
    installments: 1,
    recurringMonths: 12,
    // Day of month (1–28) the standing order re-charges. Defaults to today,
    // capped at 28 so every month has that day. First charge runs immediately.
    chargingDay: Math.min(28, new Date().getDate()),
    checkNumber: '',
    checkBank: '',
    checkBranch: '',
    checkAccount: '',
    checkDueDate: today,
    checkOwnerName: '',
    checkOwnerIdNumber: '',
    bitTransactionId: '',
    donationDate: today,
    notes: '',
    purpose: '',
    campaignId: '',
    ambassadorSlug: '',
  });

  const resetForm = useCallback(() => {
    setForm({
      contactId: '',
      amount: 0,
      paymentMethod: 'credit_card',
      isRecurring: false,
      bankName: '', bankBranch: '', bankAccount: '', transferDate: today, transferReference: '',
      masavBankCode: '', masavBranchCode: '', masavAccountNumber: '', masavAccountName: '', masavIdNumber: '',
      masavBillingDay: 15, masavInstitutionId: '', masavIdType: 'individual',
      masavIsRecurring: true, masavMonths: 12,
      holderIdNumber: '', installments: 1, recurringMonths: 12,
      chargingDay: Math.min(28, new Date().getDate()),
      checkNumber: '', checkBank: '', checkBranch: '', checkAccount: '', checkDueDate: today,
      checkOwnerName: '', checkOwnerIdNumber: '',
      bitTransactionId: '',
      donationDate: today, notes: '', purpose: '',
      campaignId: defaultCampaignId,
      ambassadorSlug: '',
    });
    setShowNewContact(false);
    setNewContact({ firstName: '', lastName: '', phone: '', email: '', nationalId: '' });
    setContactSearch('');
    setPickerOpen(false);
    setSelectedContact(null);
    setContactResults([]);
    setContactsTotal(0);
    setAssociationMode('campaign');
    setFamilySearch('');
    setFamilyPickerOpen(false);
    setSelectedFamily(null);
    setFamilyResults([]);
    setChargeReceiptMessage(orgReceiptMessage);
    setChargeSection46Enabled(true);
    setReceiptMode('none');
    setVerifyOnly(false);
    setVerifyStartDate('');
    setUploadedReceiptFile(null);
    setUploadedReceiptNumber('');
    setMasavPrefilled(false);
  }, [today, defaultCampaignId, orgReceiptMessage]);

  // מס״ב configuration, loaded lazily — the vast majority of donations are not
  // מס״ב, so there's no reason to pay for this request on every dialog open.
  // Declared after `form` on purpose: the effect reads form.paymentMethod.
  const [masavConfig, setMasavConfig] = useState<null | {
    debitReady: boolean;
    institutions: Array<{ _id: string; label: string; kind: string; institutionCode: string }>;
  }>(null);
  // True once a contact's CRM bank details actually filled something in, so the
  // block can say where the numbers came from.
  const [masavPrefilled, setMasavPrefilled] = useState(false);

  useEffect(() => {
    if (!open || form.paymentMethod !== 'masav' || masavConfig) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/masav/config-status');
        if (cancelled) return;
        const cfg = {
          debitReady: !!res.data?.debitReady,
          institutions: Array.isArray(res.data?.institutions) ? res.data.institutions : [],
        };
        setMasavConfig(cfg);
        // With exactly one debit institution there's nothing to choose — preselect
        // it so the (still visible) picker starts valid.
        const debits = cfg.institutions.filter((i: any) => i.kind === 'debit');
        if (debits.length === 1) {
          setForm(prev => ({ ...prev, masavInstitutionId: prev.masavInstitutionId || debits[0]._id }));
        }
      } catch {
        if (!cancelled) setMasavConfig({ debitReady: false, institutions: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [open, form.paymentMethod, masavConfig]);

  const masavDebitInstitutions = (masavConfig?.institutions || []).filter(i => i.kind === 'debit');

  // Pull the bank details the CRM already holds for a contact into the מס״ב
  // block. Only empty fields are touched — whatever the operator typed wins.
  const prefillMasavFromContact = useCallback(async (contactId: string) => {
    try {
      const res = await api.get(`/masav/contact-bank/${contactId}`);
      if (!res.data?.hasBank) return;
      const bank = res.data.bankAccount || {};
      const patch: Record<string, string> = {};
      if (!form.masavBankCode && bank.bankCode) patch.masavBankCode = String(bank.bankCode);
      if (!form.masavBranchCode && bank.branchCode) patch.masavBranchCode = String(bank.branchCode);
      if (!form.masavAccountNumber && bank.accountNumber) patch.masavAccountNumber = String(bank.accountNumber);
      if (!form.masavAccountName && bank.accountName) patch.masavAccountName = String(bank.accountName);
      if (!form.masavIdNumber && res.data.idNumber) patch.masavIdNumber = String(res.data.idNumber);
      if (Object.keys(patch).length === 0) return;
      setForm(prev => ({ ...prev, ...patch }));
      setMasavPrefilled(true);
    } catch { /* non-fatal — the operator can type the details manually */ }
  }, [form.masavBankCode, form.masavBranchCode, form.masavAccountNumber, form.masavAccountName, form.masavIdNumber]);

  // Close the contact picker when clicking outside of it.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [pickerOpen]);

  // Close the campaign picker when clicking outside of it.
  useEffect(() => {
    if (!campaignPickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (campaignPickerRef.current && !campaignPickerRef.current.contains(e.target as Node)) {
        setCampaignPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [campaignPickerOpen]);

  // Close the ambassador picker when clicking outside of it.
  useEffect(() => {
    if (!ambassadorPickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ambassadorPickerRef.current && !ambassadorPickerRef.current.contains(e.target as Node)) {
        setAmbassadorPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [ambassadorPickerOpen]);

  // Close the tuition-family picker when clicking outside of it.
  useEffect(() => {
    if (!familyPickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (familyPickerRef.current && !familyPickerRef.current.contains(e.target as Node)) {
        setFamilyPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [familyPickerOpen]);

  // Debounced server-side tuition-family search. Mirrors the contact picker
  // below: 250 ms debounce + AbortController so a slow earlier request can't
  // overwrite a newer one. Note the query param is `q` (not `search`) — that's
  // what GET /tuition/families filters on; it returns a bare array.
  useEffect(() => {
    if (!open || associationMode !== 'tuition' || !familyPickerOpen) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setFamiliesLoading(true);
      try {
        const res = await api.get('/tuition/families', {
          params: { q: familySearch.trim() || undefined, limit: 10 },
          signal: controller.signal as any,
        });
        const data = res.data;
        const list: TuitionFamily[] = Array.isArray(data) ? data : (data?.families || data?.data || []);
        setFamilyResults(list);
      } catch (err: any) {
        if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('[NewDonationDialog] tuition family search failed', err);
        }
      } finally {
        setFamiliesLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [familySearch, familyPickerOpen, associationMode, open]);

  // Debounced server-side contact search. Runs while the dialog is open and
  // the picker is visible; fires on every keystroke after a 250 ms pause.
  // Previous in-flight requests are aborted to avoid out-of-order results.
  useEffect(() => {
    if (!open || !pickerOpen) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setContactsLoading(true);
      try {
        const res = await api.get('/contacts', {
          params: {
            search: contactSearch.trim() || undefined,
            limit: CONTACT_PAGE_SIZE,
            page: 1,
          },
          signal: controller.signal as any,
        });
        const data = res.data;
        const list: Contact[] = Array.isArray(data) ? data : (data?.contacts || data?.data || []);
        setContactResults(list);
        const total = parseInt((res.headers as any)['x-total-count'] || '0', 10);
        setContactsTotal(Number.isFinite(total) ? total : list.length);
      } catch (err: any) {
        if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('[NewDonationDialog] contact search failed', err);
        }
      } finally {
        setContactsLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [contactSearch, pickerOpen, open]);

  // Text shown in the picker input: the search query while typing / after "new",
  // otherwise the selected contact's display name.
  const pickerDisplayValue = showNewContact
    ? ([newContact.firstName, newContact.lastName].filter(Boolean).join(' ') || '+ איש קשר חדש')
    : (pickerOpen || !selectedContact ? contactSearch : selectedContact.name);

  // Initial data load — runs once the first time the dialog opens.
  // Note: contacts are NOT loaded up-front. The org may have millions of
  // records, so the picker fetches a paginated server-side slice only when
  // the user opens it (see the debounced search effect above).
  const loadedRef = useRef(false);
  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const [orgRes, campRes] = await Promise.all([
          api.get('/organizations/me'),
          api.get('/campaigns').catch(() => ({ data: [] as any[] })),
        ]);
        const d = orgRes.data || {};
        const dcId = d.defaultCampaignId || '';
        setDefaultCampaignId(dcId);
        setForm(prev => ({ ...prev, campaignId: prev.campaignId || dcId }));
        setOrgReceiptMessage(String(d.receiptMessage || ''));
        setChargeReceiptMessage(String(d.receiptMessage || ''));
        const list = Array.isArray(campRes.data) ? campRes.data : [];
        setCampaigns(list.map((c: any) => ({
          _id: c._id,
          name: c.name || '(ללא שם)',
          status: c.status,
          ambassadors: Array.isArray(c.ambassadors)
            ? c.ambassadors
                .filter((a: any) => a && a.slug)
                .map((a: any) => ({ slug: String(a.slug), name: String(a.name || a.slug) }))
            : [],
        })));
      } catch { /* non-fatal */ }
    })();
  }, [open]);

  const closeDialog = useCallback(() => {
    if (successAutoCloseTimer.current) clearTimeout(successAutoCloseTimer.current);
    setDonationSuccessData(null);
    setReceiptResult(null);
    setIframeOpen(false);
    setIframeUrl(null);
    setPendingSessionId(null);
    onClose();
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard against a double-submit (fast double-click / Enter+click) creating
    // two identical donation rows — the credit-card path already guards via
    // chargeLoading; the offline POST /donations path had no such guard.
    if (submitting) return;
    try {
      // בשיוך לשכר לימוד אין חובת קמפיין — השרת נופל לקמפיין ברירת המחדל של
      // הארגון לצורכי הנהלת חשבונות; מה שחייב להיבחר כאן הוא המשפחה.
      if (associationMode === 'tuition') {
        if (!selectedFamily) {
          alert('יש לבחור משפחה לשיוך שכר הלימוד');
          return;
        }
      } else if (!form.campaignId) {
        alert('יש לבחור קמפיין');
        return;
      }
      // Every donation must be attached to a donor — either an existing contact
      // or a new one — so it never lands as a nameless/orphan row.
      if (!showNewContact && !form.contactId) {
        alert('יש לבחור איש קשר להכנסה, או ליצור איש קשר חדש');
        return;
      }
      setSubmitting(true);
      let contactId = form.contactId;
      let resolvedDonorName = '';
      let resolvedDonorPhone = '';
      let resolvedDonorEmail = '';
      let resolvedDonorId = '';

      if (showNewContact) {
        const firstName = newContact.firstName.trim();
        const lastName = newContact.lastName.trim();
        if (!firstName && !lastName) {
          alert('צריך להזין שם פרטי לאיש הקשר החדש');
          return;
        }
        const fullName = [firstName, lastName].filter(Boolean).join(' ')
          || newContact.email.trim() || newContact.phone.trim();
        const contactResponse = await api.post('/contacts', {
          firstName,
          lastName,
          name: fullName,
          phone: newContact.phone.trim(),
          email: newContact.email.trim(),
          nationalId: newContact.nationalId.trim() || undefined,
          type: 'donor',
        });
        const createdContact = contactResponse.data;
        contactId = createdContact._id;
        resolvedDonorName = fullName;
        resolvedDonorPhone = newContact.phone.trim();
        resolvedDonorEmail = newContact.email.trim();
        resolvedDonorId = newContact.nationalId.trim();
        setSelectedContact(createdContact);
        setContactResults(prev => [createdContact, ...prev]);
      } else {
        const foundContact = selectedContact && selectedContact._id === contactId
          ? selectedContact
          : contactResults.find(c => c._id === contactId);
        resolvedDonorName = (foundContact as any)?.name || '';
        resolvedDonorPhone = (foundContact as any)?.phone || '';
        resolvedDonorEmail = (foundContact as any)?.email || '';
        resolvedDonorId = (foundContact as any)?.idNumber || (foundContact as any)?.nationalId || '';
      }

      // מס״ב is a mandate lifecycle, not a payment method: nothing is received
      // yet, so no Donation is created here. We open a הרשאת מס״ב instead, and
      // the income row is born when the generated file is marked as transmitted.
      // (POST /donations rejects paymentMethod='masav' outright.)
      if (form.paymentMethod === 'masav') {
        if (!contactId) {
          alert('יש לבחור איש קשר — הרשאת מס״ב מוקמת על שם איש קשר');
          return;
        }
        const mandate = await api.post('/masav', {
          contactId,
          bankCode: form.masavBankCode,
          branchCode: form.masavBranchCode,
          accountNumber: form.masavAccountNumber,
          accountName: form.masavAccountName || resolvedDonorName,
          idNumber: form.masavIdNumber || undefined,
          idType: form.masavIdType,
          monthlyAmount: form.amount,
          // 1 = חיוב חד־פעמי, N = N חיובים חודשיים,
          // RECURRING_UNLIMITED = עד ביטול.
          installmentTotal: form.masavIsRecurring ? form.masavMonths : 1,
          billingDay: form.masavBillingDay,
          // The two association modes reach a mandate the same way they reach an
          // income row: a campaign is inherited by every Donation this mandate
          // produces, and a tuition family makes each settled debit pay down that
          // family's open charges through the existing settlement bridge.
          campaignId: associationMode === 'campaign' ? (form.campaignId || undefined) : undefined,
          tuitionFamilyId: associationMode === 'tuition' ? selectedFamily?._id : undefined,
          institutionId: form.masavInstitutionId || undefined,
          startDate: form.donationDate || undefined,
          notes: form.notes || undefined,
          // Write the account back onto the contact card so the CRM stays the
          // source of truth for the next mandate.
          saveBankToContact: true,
        });

        triggerRefresh();
        onSaved?.();

        setDonationSuccessData({
          id: mandate.data._id,
          amount: form.amount,
          donorName: resolvedDonorName || 'תורם',
          paymentMethod: 'masav',
          donorPhone: resolvedDonorPhone,
          masavMandate: {
            asmachta: mandate.data.asmachta,
            billingDay: mandate.data.billingDay,
            installmentTotal: form.masavIsRecurring ? form.masavMonths : 1,
          },
        });

        if (successAutoCloseTimer.current) clearTimeout(successAutoCloseTimer.current);
        // Longer than the donation card — there's a lifecycle explanation and an
        // asmachta to copy here.
        successAutoCloseTimer.current = setTimeout(() => {
          setDonationSuccessData(null);
          closeDialog();
          resetForm();
        }, 12000);
        return;
      }

      const response = await api.post('/donations', {
        ...form,
        donationDate: buildDonationTimestamp(form.donationDate),
        contactId,
        status: deriveStatus(form.paymentMethod),
        // שיוך לשכר לימוד: השרת רושם את ההכנסה על המשפחה, מסמן source='tuition'
        // וזוקף את הסכום לחיובים הפתוחים דרך המתאם המשותף.
        tuitionFamilyId: associationMode === 'tuition' ? selectedFamily?._id : undefined,
        // Prefer the card-holder ת"ז; else the donor's own ת"ז (from the new or
        // selected contact) so the receipt carries the ID even without a card field.
        donorIdNumber: form.holderIdNumber || resolvedDonorId || undefined,
        // Send the resolved donor identity so the name is stored on the donation
        // (drives receipts + the public recent-donors block), not just contactId.
        donorName: resolvedDonorName || undefined,
        donorPhone: resolvedDonorPhone || undefined,
        donorEmail: resolvedDonorEmail || undefined,
      });
      const savedDonation = response.data;

      // Receipt handling for non-credit-card donations. Failure here is
      // surfaced in the success card but doesn't block the donation save.
      let issuedReceiptNumber: string | undefined = savedDonation.receiptNumber;
      if (form.paymentMethod !== 'credit_card') {
        if (receiptMode === 'issue' && receiptTerminalId) {
          try {
            const recRes = await api.post(`/receipts/generate/${savedDonation._id}`, {
              terminalId: receiptTerminalId,
              receiptKind,
            });
            issuedReceiptNumber = recRes.data?.receiptNumber || issuedReceiptNumber;
            setReceiptResult({
              ok: true,
              message: `קבלה ${recRes.data?.receiptNumber ? '#' + recRes.data.receiptNumber + ' ' : ''}הופקה דרך ${recRes.data?.provider === 'tranzila' ? 'Tranzila' : 'HyPay'}`,
            });
          } catch (recErr: any) {
            setReceiptResult({
              ok: false,
              error: recErr?.response?.data?.error || 'שגיאה בהפקת הקבלה',
            });
          }
        } else if (receiptMode === 'upload' && uploadedReceiptFile) {
          try {
            const fd = new FormData();
            fd.append('file', uploadedReceiptFile);
            if (uploadedReceiptNumber.trim()) fd.append('receiptNumber', uploadedReceiptNumber.trim());
            const upRes = await api.post(`/receipts/upload/${savedDonation._id}`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            issuedReceiptNumber = upRes.data?.receiptNumber || issuedReceiptNumber;
            setReceiptResult({ ok: true, message: 'הקבלה הועלתה ונשמרה' });
          } catch (upErr: any) {
            setReceiptResult({
              ok: false,
              error: upErr?.response?.data?.error || 'שגיאה בהעלאת הקבלה',
            });
          }
        }
      }

      triggerRefresh();
      onSaved?.();

      setDonationSuccessData({
        id: savedDonation._id,
        amount: savedDonation.amount ?? form.amount,
        donorName: savedDonation.donorName || resolvedDonorName || 'תורם',
        paymentMethod: savedDonation.paymentMethod || form.paymentMethod,
        receiptNumber: issuedReceiptNumber,
        donorPhone: resolvedDonorPhone,
        ...(associationMode === 'tuition' && selectedFamily
          ? { tuition: { familyName: selectedFamily.displayName, applied: Number(savedDonation.tuitionApplied) || 0 } }
          : {}),
      });

      if (successAutoCloseTimer.current) clearTimeout(successAutoCloseTimer.current);
      successAutoCloseTimer.current = setTimeout(() => {
        setDonationSuccessData(null);
        closeDialog();
        resetForm();
      }, 8000);
    } catch (error: any) {
      console.error('Failed to create donation:', error);
      // Surface the server message — the מס״ב path returns actionable errors
      // ("קיימת כבר הרשאה פעילה לאיש קשר זה") that a generic string would hide.
      alert(error?.response?.data?.error || 'שגיאה ביצירת הכנסה');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualCharge = async () => {
    if (!form.amount || form.amount <= 0) {
      alert('יש להזין סכום');
      return;
    }
    if (!form.campaignId) {
      alert('יש לבחור קמפיין');
      return;
    }
    if (verifyOnly && (!form.isRecurring || !verifyStartDate)) {
      alert('חיוב עתידי (אימות בלבד) זמין רק להוראת קבע, ויש לבחור תאריך תחילת חיוב');
      return;
    }
    setChargeLoading(true);
    try {
      // If the admin chose "+ צור איש קשר חדש" but didn't submit the form first,
      // create the contact here so the charge carries the donor's details.
      let contactId = form.contactId;
      let clientName = '';
      let clientLastName = '';
      let clientEmail = '';
      let clientPhone = '';

      if (showNewContact) {
        const firstName = newContact.firstName.trim();
        const lastName = newContact.lastName.trim();
        if (!firstName && !lastName) {
          alert('צריך להזין שם פרטי לאיש הקשר החדש');
          setChargeLoading(false);
          return;
        }
        const fullName = [firstName, lastName].filter(Boolean).join(' ')
          || newContact.email.trim() || newContact.phone.trim();
        const contactResponse = await api.post('/contacts', {
          firstName,
          lastName,
          name: fullName,
          phone: newContact.phone.trim(),
          email: newContact.email.trim(),
          nationalId: newContact.nationalId.trim() || undefined,
          type: 'donor',
        });
        const createdContact = contactResponse.data;
        contactId = createdContact._id;
        clientName = firstName || fullName;
        clientLastName = lastName;
        clientEmail = newContact.email.trim();
        clientPhone = newContact.phone.trim();
        setSelectedContact(createdContact);
        setContactResults(prev => [createdContact, ...prev]);
        setForm(prev => ({ ...prev, contactId }));
        setShowNewContact(false);
      } else {
        const contactObj = selectedContact && selectedContact._id === contactId
          ? selectedContact
          : contactResults.find(c => c._id === contactId);
        const c = contactObj as any;
        if (c?.firstName || c?.lastName) {
          clientName = (c.firstName || '').trim();
          clientLastName = (c.lastName || '').trim();
        } else {
          // Mirror PublicCampaign.tsx: split the full name on the first space.
          const full = (c?.name || '').trim();
          const idx = full.indexOf(' ');
          if (idx === -1) {
            clientName = full;
          } else {
            clientName = full.slice(0, idx);
            clientLastName = full.slice(idx + 1).trim();
          }
        }
        clientEmail = c?.email || '';
        clientPhone = c?.phone || '';
      }

      const res = await api.post('/payments/manual-charge', {
        amount: form.amount,
        contactId: contactId || undefined,
        campaignId: form.campaignId || undefined,
        clientName,
        clientLastName: clientLastName || undefined,
        clientEmail,
        clientPhone,
        notes: form.notes || '',
        isRecurring: form.isRecurring,
        installments: form.installments,
        recurringMonths: form.isRecurring ? form.recurringMonths : undefined,
        chargingDay: form.isRecurring ? form.chargingDay : undefined,
        // Future-dated standing order: only for recurring charges on a Tranzila
        // terminal, and only when the admin picked a start date. The backend
        // runs a tranmode=N verification (no charge now) and schedules the
        // first charge for verifyStartDate.
        verifyOnly: Boolean(form.isRecurring && verifyOnly && verifyStartDate),
        startDate: form.isRecurring && verifyOnly && verifyStartDate ? verifyStartDate : undefined,
        section46: chargeSection46Enabled,
        receiptMessage: chargeReceiptMessage || undefined,
        // Receipt kind chosen by the admin in this dialog (overrides any
        // campaign default). Persisted on the PaymentSession so the iframe
        // callback uses it when issuing the EZcount document.
        receiptKind,
        tmp: 3,
        terminalId: chargeTerminalId || undefined,
        ambassadorSlug: form.ambassadorSlug || undefined,
      });
      const { url, sessionId } = res.data;
      setPendingSessionId(sessionId);
      setIframeUrl(url);
      setIframeOpen(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'שגיאה ביצירת קישור תשלום';
      if (err?.response?.data?.requiresSetup) {
        alert('⚠️ ' + msg + '\n\nיש להגדיר מסוף סליקה בהגדרות הארגון.');
      } else {
        alert(msg);
      }
    } finally {
      setChargeLoading(false);
    }
  };

  const handleIframePaymentSuccess = useCallback(async (payload: Record<string, string> = {}) => {
    setIframeOpen(false);
    setIframeUrl(null);
    // Always render the success card on a confirmed payment — Hyp redirected
    // /payments/complete inside the iframe and PaymentSuccessPage posted back
    // to us, so the charge succeeded. We start with what we already know
    // from the form + the postMessage payload, then enrich with backend data
    // if /payments/session-status has it (for receipt number etc.).
    const fallbackAmount = Number(payload.amount) || form.amount;
    let success = {
      id: payload.tx || pendingSessionId || '',
      amount: fallbackAmount,
      donorName: selectedContact?.name || 'תורם',
      paymentMethod: 'credit_card',
      receiptNumber: undefined as string | undefined,
      donorPhone: selectedContact?.phone || '',
    };
    try {
      if (pendingSessionId) {
        const statusRes = await api.get(`/payments/session-status/${pendingSessionId}`);
        const donationId = statusRes.data?.donationId;
        if (donationId) {
          try {
            const donationRes = await api.get(`/donations/${donationId}`);
            const d = donationRes.data || {};
            success = {
              id: donationId,
              amount: d.amount ?? fallbackAmount,
              donorName: d.donorName || success.donorName,
              paymentMethod: d.paymentMethod || 'credit_card',
              receiptNumber: d.receiptNumber,
              donorPhone: d.donorPhone || success.donorPhone,
            };
          } catch { /* non-fatal */ }
        }
      }
    } catch { /* non-fatal */ }
    setDonationSuccessData(
      verifyOnly && verifyStartDate
        ? { ...success, futureStandingOrder: { startDate: verifyStartDate } }
        : success
    );
    setPendingSessionId(null);
    triggerRefresh();
    onSaved?.();
  }, [pendingSessionId, form.amount, selectedContact, triggerRefresh, onSaved, verifyOnly, verifyStartDate]);

  const handleIframePaymentFail = useCallback((payload: Record<string, string>) => {
    setIframeOpen(false);
    setIframeUrl(null);
    setPendingSessionId(null);
    const msg = payload?.error || payload?.errMsg || 'התשלום לא הושלם';
    alert('⚠️ ' + msg);
  }, []);

  const generateReceiptFor = async (donationId: string) => {
    try {
      const res = await api.post(`/receipts/generate/${donationId}`);
      if (res.data?.pdfUrl) {
        window.open(res.data.pdfUrl, '_blank');
        return { ok: true, receiptNumber: res.data?.receiptNumber, provider: res.data?.provider };
      }
      if (res.data?.provider === 'hyp' && res.data?.message) {
        return { ok: true, receiptNumber: res.data?.receiptNumber, message: res.data.message };
      }
      if (res.data?.pdfBase64) {
        const blob = new Blob(
          [Uint8Array.from(atob(res.data.pdfBase64), c => c.charCodeAt(0))],
          { type: 'application/pdf' }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${res.data.receiptNumber || donationId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
      return { ok: true, receiptNumber: res.data?.receiptNumber };
    } catch (err: any) {
      return { ok: false, error: err?.response?.data?.error || 'שגיאה בהפקת קבלה' };
    }
  };

  if (!open) {
    // keep the iframe modal mounted even when dialog closes, in case a charge
    // is in-flight and the user closes the modal wrapping it — unlikely but safe.
    return null;
  }

  // Future-dated standing orders (verify-only / tranmode=N) are a Tranzila-only
  // capability — HyP has a different tokenization path. Gate the UI on the
  // selected charge terminal's provider.
  const isTranzilaCharge =
    (terminalOptions.find((t) => t._id === chargeTerminalId)?.provider || '') === 'tranzila';
  // Earliest allowed first-charge date = tomorrow (a future-dated order can't
  // start today — that's just an immediate charge).
  const minStartDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" dir="rtl">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 md:p-6 w-full md:max-w-2xl mx-4 my-8 overflow-y-auto max-h-[92vh]">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">הוספת הכנסה חדשה</h3>
            <button onClick={closeDialog} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
              <X size={20} />
            </button>
          </div>

          {donationSuccessData ? (
            <div className="text-center py-6 space-y-4">
              <div className="text-6xl">{donationSuccessData.masavMandate ? '🏦' : donationSuccessData.futureStandingOrder ? '🗓️' : '✅'}</div>
              <h4 className="text-2xl font-bold text-green-600 dark:text-green-400">
                {donationSuccessData.masavMandate
                  ? 'הרשאת המס״ב נוצרה!'
                  : donationSuccessData.futureStandingOrder ? 'הוראת הקבע הוקמה בהצלחה!' : 'ההכנסה נשמרה בהצלחה!'}
              </h4>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-right space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">
                    {/* חיוב מס״ב חד־פעמי אינו "סכום חודשי" — התווית מתחלפת. */}
                    {(donationSuccessData.masavMandate && donationSuccessData.masavMandate.installmentTotal !== 1) || donationSuccessData.futureStandingOrder
                      ? 'סכום חודשי:'
                      : 'סכום:'}
                  </span>
                  <span className="font-bold text-green-600 dark:text-green-400">₪{Number(donationSuccessData.amount).toLocaleString()}</span>
                </div>
                {donationSuccessData.masavMandate && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">סוג החיוב:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {donationSuccessData.masavMandate.installmentTotal === 1
                          ? 'חיוב חד־פעמי'
                          : donationSuccessData.masavMandate.installmentTotal >= RECURRING_UNLIMITED
                            ? 'הוראת קבע ללא הגבלה'
                            : `${donationSuccessData.masavMandate.installmentTotal} תשלומים חודשיים`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">יום חיוב:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {donationSuccessData.masavMandate.billingDay} בכל חודש
                      </span>
                    </div>
                  </>
                )}
                {donationSuccessData.futureStandingOrder && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">חיוב ראשון:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                      {new Date(donationSuccessData.futureStandingOrder.startDate).toLocaleDateString('he-IL')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">תורם:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{donationSuccessData.donorName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">אמצעי תשלום:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{PAYMENT_METHOD_LABELS[donationSuccessData.paymentMethod] || donationSuccessData.paymentMethod}</span>
                </div>
                {donationSuccessData.receiptNumber && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">מספר קבלה:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">#{donationSuccessData.receiptNumber}</span>
                  </div>
                )}
              </div>

              {/* משפחה ללא חיובים פתוחים היא תוצאה תקינה לגמרי — מדווחים
                  אותה במפורש, כי "כלום" על המסך נראה כמו תקלה. */}
              {donationSuccessData.tuition && (
                <div className="text-right text-xs bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-4 space-y-1 text-purple-800 dark:text-purple-300">
                  <p className="flex items-center justify-between gap-2">
                    <span className="text-gray-500 dark:text-gray-400">משפחה:</span>
                    <span className="font-semibold">🎓 {donationSuccessData.tuition.familyName}</span>
                  </p>
                  <p>
                    {donationSuccessData.tuition.applied > 0
                      ? <>₪{donationSuccessData.tuition.applied.toLocaleString('he-IL')} נזקפו לחיובי שכר הלימוד</>
                      : 'לא נמצאו חיובים פתוחים לזקיפה — ההכנסה נרשמה על המשפחה בלבד'}
                  </p>
                </div>
              )}

              {/* No Donation exists yet — spell out what still has to happen
                  before this money shows up as income. */}
              {donationSuccessData.masavMandate && (
                <div className="text-right text-xs bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-xl p-4 space-y-2 text-indigo-800 dark:text-indigo-300">
                  <p>
                    ההרשאה ממתינה במסך <strong>מס״ב</strong> בסטטוס ״ממתין להקמה״. ההכנסה תירשם
                    רק לאחר שההרשאה תופעל, יופק קובץ מס״ב והקובץ יסומן כמשודר.
                  </p>
                  <p className="flex items-center justify-between gap-2 pt-1 border-t border-indigo-200 dark:border-indigo-700">
                    <span className="text-gray-500 dark:text-gray-400">אסמכתא למסירה לתורם:</span>
                    <span className="font-mono font-bold">{donationSuccessData.masavMandate.asmachta}</span>
                  </p>
                  <a
                    href="/masav"
                    className="inline-block font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    ← מעבר למסך מס״ב
                  </a>
                </div>
              )}

              {receiptResult && (
                <div className={`text-sm px-4 py-2 rounded-lg ${receiptResult.ok ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                  {receiptResult.ok ? (receiptResult.message || '✅ הקבלה הופקה בהצלחה') : (receiptResult.error || 'שגיאה בהפקת קבלה')}
                </div>
              )}

              {receiptResult?.ok && donationSuccessData.donorPhone && (
                <a
                  href={`https://wa.me/${donationSuccessData.donorPhone.replace(/\D/g, '')}?text=${encodeURIComponent(`שלום! קבלה על תרומה בסך ₪${donationSuccessData.amount} ממתינה לך.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-xl transition-colors"
                >
                  📲 שלח ב-WhatsApp
                </a>
              )}

              <div className="flex gap-3 justify-center mt-4">
                {/* Hide "הפק קבלה" for credit-card (סליקה) charges — the terminal
                    (HyP/Tranzila) issues the receipt automatically right after
                    the charge, so a manual button here is redundant. Also hidden
                    for a future-dated standing order (no charge ran yet) and for
                    a מס״ב mandate (there is no Donation to receipt at all). It
                    stays only for offline methods (cash/check/transfer), which
                    have no automatic clearing receipt. */}
                {!donationSuccessData.futureStandingOrder && !donationSuccessData.masavMandate && donationSuccessData.paymentMethod !== 'credit_card' && (
                  <button
                    type="button"
                    disabled={receiptGenerating}
                    onClick={async () => {
                      if (successAutoCloseTimer.current) clearTimeout(successAutoCloseTimer.current);
                      setReceiptGenerating(true);
                      const result = await generateReceiptFor(donationSuccessData.id);
                      setReceiptResult(result as any);
                      setReceiptGenerating(false);
                    }}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white transition-colors flex items-center gap-2"
                  >
                    {receiptGenerating ? <><span className="animate-spin">⏳</span> מפיק...</> : '🧾 הפק קבלה'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { closeDialog(); resetForm(); }}
                  className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  סגור
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div ref={pickerRef} className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">איש קשר *</label>
                  <input
                    type="text"
                    value={pickerDisplayValue}
                    onChange={e => {
                      setContactSearch(e.target.value);
                      setPickerOpen(true);
                      if (form.contactId) setForm({ ...form, contactId: '' });
                      if (showNewContact) setShowNewContact(false);
                    }}
                    onFocus={() => setPickerOpen(true)}
                    placeholder="חיפוש לפי שם / טלפון / מייל..."
                    autoComplete="off"
                    required={!showNewContact && !form.contactId}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {pickerOpen && (
                    <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewContact(true);
                          setForm({ ...form, contactId: '' });
                          setSelectedContact(null);
                          setContactSearch('');
                          setPickerOpen(false);
                        }}
                        className="w-full text-right px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700"
                      >
                        + צור איש קשר חדש
                      </button>
                      {contactsLoading && contactResults.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                          <span className="animate-spin">⏳</span> טוען...
                        </div>
                      ) : contactResults.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-gray-500 text-center">
                          {contactSearch ? 'לא נמצאו אנשי קשר' : 'התחילו להקליד כדי לחפש...'}
                        </div>
                      ) : (
                        <>
                          {contactResults.map(c => (
                            <button
                              key={c._id}
                              type="button"
                              onClick={() => {
                                setForm({ ...form, contactId: c._id });
                                setSelectedContact(c);
                                setShowNewContact(false);
                                setContactSearch(c.name);
                                setPickerOpen(false);
                                // The CRM card is the source of truth for the
                                // debit-side bank details — pull them in so the
                                // operator doesn't retype them.
                                if (form.paymentMethod === 'masav') prefillMasavFromContact(c._id);
                              }}
                              className="w-full text-right px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <div className="font-medium">{c.name}</div>
                              {(c.phone || c.email) && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {[c.phone, c.email].filter(Boolean).join(' • ')}
                                </div>
                              )}
                            </button>
                          ))}
                          {contactsTotal > contactResults.length && (
                            <div className="px-3 py-2 text-center text-xs text-gray-500 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                              מוצגים {contactResults.length} מתוך {contactsTotal.toLocaleString()} — חפשו ספציפי יותר כדי לצמצם
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">סכום *</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={form.amount || ''}
                    onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                {/* שיוך ההכנסה — לקמפיין או לשכר לימוד. שני המצבים סותרים:
                    בקמפיין נשלח campaignId, בשכר לימוד נשלח tuitionFamilyId
                    והשרת בוחר את קמפיין ברירת המחדל בעצמו. */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">שיוך</label>
                  <div className="flex flex-col sm:flex-row gap-2 text-sm">
                    {[
                      { v: 'campaign' as const, label: 'קמפיין' },
                      { v: 'tuition' as const, label: '🎓 שכר לימוד' },
                    ].map((opt) => (
                      <label
                        key={opt.v}
                        className={`flex-1 px-3 py-2 rounded-lg border cursor-pointer text-center transition-colors ${
                          associationMode === opt.v
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 font-medium'
                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="associationMode"
                          value={opt.v}
                          checked={associationMode === opt.v}
                          onChange={() => {
                            setAssociationMode(opt.v);
                            // כל מצב מנקה את הבחירה של השני, כדי שלא תישאר
                            // בחירה "רדומה" שנשלחת בטעות.
                            setCampaignPickerOpen(false);
                            setFamilyPickerOpen(false);
                            if (opt.v === 'campaign') {
                              setSelectedFamily(null);
                              setFamilySearch('');
                              setFamilyResults([]);
                            } else {
                              setForm(prev => ({ ...prev, ambassadorSlug: '' }));
                            }
                          }}
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  {associationMode === 'tuition' && form.paymentMethod === 'credit_card' && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      ⚠️ שיוך לשכר לימוד נתמך באמצעי תשלום שאינם סליקת אשראי. לגביית אשראי בחרו שיוך לקמפיין.
                    </p>
                  )}
                </div>

                {associationMode === 'campaign' ? (
                <div ref={campaignPickerRef} className="sm:col-span-2 relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">קמפיין *</label>
                  <input
                    type="text"
                    value={campaignPickerOpen
                      ? campaignSearch
                      : (campaigns.find(c => c._id === form.campaignId)?.name || '')}
                    onChange={e => { setCampaignSearch(e.target.value); setCampaignPickerOpen(true); }}
                    onFocus={() => { setCampaignSearch(''); setCampaignPickerOpen(true); }}
                    placeholder="חיפוש קמפיין..."
                    autoComplete="off"
                    required={!form.campaignId}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {campaignPickerOpen && (
                    <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
                      {(() => {
                        const q = campaignSearch.trim().toLowerCase();
                        const filtered = q
                          ? campaigns.filter(c => (c.name || '').toLowerCase().includes(q))
                          : campaigns;
                        if (filtered.length === 0) {
                          return (
                            <div className="px-3 py-3 text-sm text-gray-500 text-center">
                              {campaigns.length === 0 ? 'אין קמפיינים' : 'לא נמצאו קמפיינים תואמים'}
                            </div>
                          );
                        }
                        return filtered.map(c => (
                          <button
                            key={c._id}
                            type="button"
                            onClick={() => {
                              setForm({ ...form, campaignId: c._id, ambassadorSlug: '' });
                              setCampaignSearch('');
                              setCampaignPickerOpen(false);
                              setAmbassadorSearch('');
                              setAmbassadorPickerOpen(false);
                            }}
                            className="w-full text-right px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <span className="font-medium">{c.name}</span>
                            {c.status && c.status !== 'active' && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">({c.status})</span>
                            )}
                          </button>
                        ));
                      })()}
                    </div>
                  )}
                  {campaigns.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      לא נמצאו קמפיינים — צרו קמפיין במסך הקמפיינים לפני הוספת הכנסה.
                    </p>
                  )}
                </div>
                ) : (
                <div ref={familyPickerRef} className="sm:col-span-2 relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">🎓 משפחה *</label>
                  <input
                    type="text"
                    value={familyPickerOpen ? familySearch : (selectedFamily?.displayName || '')}
                    onChange={e => { setFamilySearch(e.target.value); setFamilyPickerOpen(true); }}
                    onFocus={() => { setFamilySearch(''); setFamilyPickerOpen(true); }}
                    placeholder="חיפוש לפי שם משפחה / הורה / תלמיד..."
                    autoComplete="off"
                    required={!selectedFamily}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {familyPickerOpen && (
                    <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
                      {familiesLoading && familyResults.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                          <span className="animate-spin">⏳</span> טוען...
                        </div>
                      ) : familyResults.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-gray-500 text-center">
                          {familySearch ? 'לא נמצאו משפחות' : 'התחילו להקליד כדי לחפש...'}
                        </div>
                      ) : (
                        familyResults.map(f => (
                          <button
                            key={f._id}
                            type="button"
                            onClick={() => {
                              setSelectedFamily(f);
                              setFamilySearch('');
                              setFamilyPickerOpen(false);
                            }}
                            className="w-full text-right px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <div className="font-medium">{f.displayName || '(ללא שם)'}</div>
                            {(f.studentNames || []).length > 0 && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {(f.studentNames || []).join(' • ')}
                              </div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    ההכנסה תיזקף לחיובי שכר הלימוד הפתוחים של המשפחה.
                  </p>
                </div>
                )}

                {/* Ambassador attribution — searchable dropdown, shown only
                    after a campaign with ambassadors is selected. Saved on the
                    donation so the ambassador meter (which aggregates donations
                    by ambassadorSlug) credits them. Works for every payment
                    method: the offline POST /donations path and the credit-card
                    /payments/manual-charge path both carry the slug. */}
                {(() => {
                  const selectedCampaign = campaigns.find(c => c._id === form.campaignId);
                  const ambassadors = selectedCampaign?.ambassadors || [];
                  if (associationMode !== 'campaign' || !form.campaignId || ambassadors.length === 0) return null;
                  const selectedAmb = ambassadors.find(a => a.slug === form.ambassadorSlug);
                  const q = ambassadorSearch.trim().toLowerCase();
                  const filtered = q
                    ? ambassadors.filter(a => a.name.toLowerCase().includes(q) || a.slug.toLowerCase().includes(q))
                    : ambassadors;
                  return (
                    <div ref={ambassadorPickerRef} className="sm:col-span-2 relative">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">🎗️ שיוך לשגריר</label>
                      <input
                        type="text"
                        value={ambassadorPickerOpen ? ambassadorSearch : (selectedAmb?.name || '')}
                        onChange={e => { setAmbassadorSearch(e.target.value); setAmbassadorPickerOpen(true); }}
                        onFocus={() => { setAmbassadorSearch(''); setAmbassadorPickerOpen(true); }}
                        placeholder="ללא שגריר — חיפוש שגריר..."
                        autoComplete="off"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      {ambassadorPickerOpen && (
                        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
                          <button
                            type="button"
                            onClick={() => {
                              setForm({ ...form, ambassadorSlug: '' });
                              setAmbassadorSearch('');
                              setAmbassadorPickerOpen(false);
                            }}
                            className="w-full text-right px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700"
                          >
                            ללא שגריר
                          </button>
                          {filtered.length === 0 ? (
                            <div className="px-3 py-3 text-sm text-gray-500 text-center">לא נמצאו שגרירים תואמים</div>
                          ) : (
                            filtered.map(a => (
                              <button
                                key={a.slug}
                                type="button"
                                onClick={() => {
                                  setForm({ ...form, ambassadorSlug: a.slug });
                                  setAmbassadorSearch('');
                                  setAmbassadorPickerOpen(false);
                                }}
                                className="w-full text-right px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                {a.name}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {showNewContact && (
                  <div className="sm:col-span-2 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <h4 className="sm:col-span-2 text-md font-semibold mb-1 text-gray-800 dark:text-gray-200">פרטי איש קשר חדש</h4>
                    <input type="text" placeholder="שם פרטי *" value={newContact.firstName} onChange={e => setNewContact({ ...newContact, firstName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
                    <input type="text" placeholder="שם משפחה" value={newContact.lastName} onChange={e => setNewContact({ ...newContact, lastName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <input type="text" placeholder="טלפון" value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <input type="email" placeholder="דוא״ל" value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <input type="text" placeholder="ת.ז / ח.פ" value={newContact.nationalId} onChange={e => setNewContact({ ...newContact, nationalId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">אמצעי תשלום</label>
                  <select
                    value={form.paymentMethod}
                    onChange={e => {
                      setForm({ ...form, paymentMethod: e.target.value as any, isRecurring: false });
                      // Switching to מס״ב after the contact was already picked —
                      // same prefill, just triggered from the other side.
                      if (e.target.value === 'masav' && form.contactId) prefillMasavFromContact(form.contactId);
                      // מס״ב hides the receipt block entirely (no money collected
                      // yet), so drop anything the operator already chose there —
                      // otherwise a stale mode/file would survive the switch.
                      if (e.target.value === 'masav') {
                        setReceiptMode('none');
                        setUploadedReceiptFile(null);
                        setUploadedReceiptNumber('');
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {PAYMENT_METHOD_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  {form.paymentMethod === 'check' && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">⏳ הצ׳ק יסומן כ׳ממתין׳ עד לפירעון</p>
                  )}
                  {form.paymentMethod === 'masav' && (
                    <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">🏦 תיווצר הרשאת מס״ב במסך מס״ב — ההכנסה תירשם רק לאחר שידור הקובץ למס״ב</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">תאריך ההכנסה</label>
                  <input type="date" value={form.donationDate} onChange={e => setForm({ ...form, donationDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>

                {form.paymentMethod === 'bank_transfer' && (
                  <div className="sm:col-span-2 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <h4 className="sm:col-span-2 text-md font-semibold mb-1 text-gray-800 dark:text-gray-200">פרטי העברה בנקאית</h4>
                    <select value={form.bankName} onChange={e => setForm({ ...form, bankName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      <option value="">בחר בנק...</option>
                      <option>בנק לאומי</option><option>בנק הפועלים</option><option>בנק דיסקונט</option><option>בנק מזרחי טפחות</option><option>הבנק הבינלאומי</option><option>אחר</option>
                    </select>
                    <input type="text" placeholder="סניף" value={form.bankBranch} onChange={e => setForm({ ...form, bankBranch: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <input type="text" placeholder="חשבון" value={form.bankAccount} onChange={e => setForm({ ...form, bankAccount: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <input type="date" value={form.transferDate} onChange={e => setForm({ ...form, transferDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <input type="text" placeholder="אסמכתא" value={form.transferReference} onChange={e => setForm({ ...form, transferReference: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                )}

                {form.paymentMethod === 'credit_card' && (
                  <div className="sm:col-span-2 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2 flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">💳 גביית אשראי דרך Hypay</h4>
                      {form.isRecurring && (
                        <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-1 rounded-full">🔄 הוראת קבע</span>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs text-blue-800 dark:text-blue-300 mb-1 font-medium">מסוף סליקה</label>
                      {terminalOptions.length === 0 ? (
                        <div className="text-xs text-red-600 dark:text-red-400">
                          לא נמצאו מסופי HyPay/Tranzila פעילים. הוסף מסוף בהגדרות.
                        </div>
                      ) : (
                        <select
                          value={chargeTerminalId}
                          onChange={(e) => {
                            setChargeTerminalId(e.target.value);
                            // Verify/future-charge is Tranzila-only — clear it if
                            // the admin switches to a HyP terminal.
                            const prov = terminalOptions.find(t => t._id === e.target.value)?.provider;
                            if (prov !== 'tranzila') { setVerifyOnly(false); setVerifyStartDate(''); }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                          {terminalOptions.map((t) => (
                            <option key={t._id} value={t._id}>
                              {t.name} — {t.provider === 'hyp' ? 'HyPay' : t.provider === 'tranzila' ? 'Tranzila' : t.provider}
                              {t.isDefault ? ' (ברירת מחדל)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={form.isRecurring}
                        onChange={e => {
                          setForm({ ...form, isRecurring: e.target.checked, installments: e.target.checked ? 1 : form.installments });
                          // Future-dated verify mode only applies to standing orders.
                          if (!e.target.checked) { setVerifyOnly(false); setVerifyStartDate(''); }
                        }}
                        className="w-4 h-4 accent-blue-600"
                      />
                      <span className="text-sm text-blue-800 dark:text-blue-300">🔄 הוראת קבע (חיוב חוזר חודשי)</span>
                    </label>
                    {!form.isRecurring && (
                      <div className="sm:col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">מספר תשלומים</label>
                        <select
                          value={form.installments}
                          onChange={e => setForm({ ...form, installments: Number(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                          <option value={1}>תשלום אחד</option>
                          {Array.from({ length: 59 }, (_, i) => i + 2).map(n => <option key={n} value={n}>{n} תשלומים</option>)}
                        </select>
                      </div>
                    )}
                    {form.isRecurring && (
                      <>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">מספר חודשים</label>
                          <select
                            value={form.recurringMonths}
                            onChange={e => setForm({ ...form, recurringMonths: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          >
                            {/* 1–60 months, then an open-ended option. 999 is the
                                system-wide sentinel for "no limit" standing orders. */}
                            {Array.from({ length: 60 }, (_, i) => i + 1).map(m => (
                              <option key={m} value={m}>{m} חודשים</option>
                            ))}
                            <option value={RECURRING_UNLIMITED}>ללא הגבלה</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">יום החיוב בחודש</label>
                          <select
                            value={form.chargingDay}
                            onChange={e => setForm({ ...form, chargingDay: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          >
                            {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                              <option key={d} value={d}>{d} בחודש</option>
                            ))}
                          </select>
                        </div>
                        {/* Future-dated standing order — Tranzila only. Verifies
                            the card now (tranmode=N / J2) and captures the token
                            WITHOUT charging; the first real charge runs on the
                            chosen start date. */}
                        {isTranzilaCharge && (
                          <div className="sm:col-span-2">
                            <label className="flex items-center gap-3 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={verifyOnly}
                                onChange={e => {
                                  setVerifyOnly(e.target.checked);
                                  if (e.target.checked && !verifyStartDate) setVerifyStartDate(minStartDate);
                                }}
                                className="w-4 h-4 accent-blue-600"
                              />
                              <span className="text-sm text-blue-800 dark:text-blue-300">🗓️ חיוב עתידי — אימות כרטיס בלבד עכשיו (ללא חיוב), התחלת חיוב בתאריך עתידי</span>
                            </label>
                            {verifyOnly && (
                              <div className="mt-2">
                                <label className="block text-xs text-gray-500 mb-1">תאריך החיוב הראשון</label>
                                <input
                                  type="date"
                                  value={verifyStartDate}
                                  min={minStartDate}
                                  onChange={e => setVerifyStartDate(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                />
                              </div>
                            )}
                          </div>
                        )}
                        <div className="sm:col-span-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2">
                          <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                            {verifyOnly ? (
                              <>
                                🗓️ <strong>לא יבוצע חיוב עכשיו</strong> — הכרטיס יאומת בלבד. החיוב הראשון יתבצע ב-{verifyStartDate ? new Date(verifyStartDate).toLocaleDateString('he-IL') : '—'}, והחיובים הבאים ב-{form.chargingDay} לכל חודש
                                {form.recurringMonths >= RECURRING_UNLIMITED
                                  ? ', ללא הגבלת חודשים (עד ביטול).'
                                  : `, במשך ${form.recurringMonths} חודשים.`}
                              </>
                            ) : (
                              <>
                                ⚡ <strong>החיוב הראשון מתבצע מיד</strong> בעת הסליקה. החיובים הבאים יבוצעו ב-{form.chargingDay} לכל חודש
                                {form.recurringMonths >= RECURRING_UNLIMITED
                                  ? ', ללא הגבלת חודשים (עד ביטול).'
                                  : `, במשך ${form.recurringMonths} חודשים.`}
                              </>
                            )}
                          </p>
                          {form.recurringMonths < RECURRING_UNLIMITED && (
                            <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                              סה״כ: ₪{(Number(form.amount || 0) * form.recurringMonths).toLocaleString('he-IL')} לאורך {form.recurringMonths} חודשים
                            </p>
                          )}
                        </div>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={handleManualCharge}
                      disabled={chargeLoading || !form.amount}
                      className="sm:col-span-2 w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                      {chargeLoading ? <><span className="animate-spin">⏳</span> מכין...</> : (verifyOnly ? '🗓️ המשך לאימות כרטיס' : '💳 המשך לסליקה')}
                    </button>
                  </div>
                )}

                {form.paymentMethod === 'check' && (
                  <div className="sm:col-span-2 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <h4 className="sm:col-span-2 text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">🧾 פרטי צ׳ק</h4>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">מספר צ׳ק</label>
                      <input type="text" placeholder="מספר הצ׳ק" value={form.checkNumber}
                        onChange={e => setForm({ ...form, checkNumber: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">תאריך פירעון</label>
                      <input type="date" value={form.checkDueDate}
                        onChange={e => setForm({ ...form, checkDueDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">בנק</label>
                      <select value={form.checkBank}
                        onChange={e => setForm({ ...form, checkBank: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                        <option value="">בחר בנק...</option>
                        <option>בנק לאומי</option><option>בנק הפועלים</option><option>בנק דיסקונט</option>
                        <option>בנק מזרחי טפחות</option><option>הבנק הבינלאומי</option><option>בנק ירושלים</option><option>אחר</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">סניף</label>
                      <input type="text" placeholder="מספר סניף" value={form.checkBranch}
                        onChange={e => setForm({ ...form, checkBranch: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">מספר חשבון</label>
                      <input type="text" placeholder="חשבון הבנק" value={form.checkAccount}
                        onChange={e => setForm({ ...form, checkAccount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                    </div>
                    <div className="sm:col-span-2 border-t border-amber-200 dark:border-amber-700 pt-3 mt-1">
                      <p className="text-xs text-amber-700 dark:text-amber-400 mb-2 font-medium">📋 פרטי בעל הצ׳ק (לקבלה)</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">שם בעל הצ׳ק *</label>
                          <input type="text" placeholder="שם מלא של בעל הצ׳ק"
                            value={form.checkOwnerName}
                            onChange={e => setForm({ ...form, checkOwnerName: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">ת.ז / ח.פ בעל הצ׳ק</label>
                          <input type="text" inputMode="numeric" placeholder="מספר ת.ז או ח.פ"
                            value={form.checkOwnerIdNumber}
                            onChange={e => setForm({ ...form, checkOwnerIdNumber: e.target.value.replace(/\D/g, '') })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            dir="ltr" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {form.paymentMethod === 'bit' && (
                  <div className="sm:col-span-2 p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-teal-800 dark:text-teal-300 flex items-center gap-2 mb-3">📲 פרטי ביט</h4>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">מזהה עסקה (אופציונלי)</label>
                      <input type="text" placeholder="מספר עסקה מאפליקציית ביט" value={form.bitTransactionId}
                        onChange={e => setForm({ ...form, bitTransactionId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                    </div>
                  </div>
                )}

                {form.paymentMethod === 'masav' && (
                  <div className="sm:col-span-2 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <h4 className="sm:col-span-2 text-md font-semibold mb-1 text-indigo-800 dark:text-indigo-300 flex items-center gap-2">
                      🏦 פרטי חיוב מס״ב — ניכוי ישיר
                    </h4>
                    {/* The מס״ב lifecycle is not obvious to a new operator, and
                        getting it wrong means booking money that never arrived. */}
                    <p className="sm:col-span-2 -mt-2 text-xs text-indigo-700 dark:text-indigo-300 leading-relaxed">
                      ההרשאה נוצרת כ״ממתין להקמה״ ← מסמנים ״הוקמה בבנק״ (פעיל) ← מפיקים קובץ מס״ב
                      ומשדרים אותו ← רק אז נרשמת ההכנסה.
                    </p>
                    {masavConfig && !masavConfig.debitReady && (
                      <p className="sm:col-span-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 rounded-lg px-3 py-2">
                        ⚠️ לא הוגדר מוסד מס״ב לחיובים בארגון — ניתן ליצור את ההרשאה, אבל לא ניתן יהיה
                        להפיק קובץ מס״ב עד להשלמת ההגדרה בהגדרות הארגון.
                      </p>
                    )}
                    {masavPrefilled && (
                      <p className="sm:col-span-2 text-xs text-green-600 dark:text-green-400">
                        ✅ מולא מפרטי הבנק שבכרטיס איש הקשר
                      </p>
                    )}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">קוד בנק *</label>
                      <select
                        value={form.masavBankCode}
                        onChange={e => setForm({ ...form, masavBankCode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        required
                      >
                        <option value="">בחר בנק...</option>
                        <option value="10">10 — בנק לאומי</option>
                        <option value="11">11 — בנק דיסקונט</option>
                        <option value="12">12 — בנק הפועלים</option>
                        <option value="13">13 — בנק אגוד</option>
                        <option value="14">14 — בנק אוצר החייל</option>
                        <option value="17">17 — בנק מרכנתיל דיסקונט</option>
                        <option value="20">20 — בנק מזרחי טפחות</option>
                        <option value="22">22 — סיטיבנק</option>
                        <option value="23">23 — HSBC</option>
                        <option value="26">26 — יובנק</option>
                        <option value="31">31 — הבנק הבינלאומי</option>
                        <option value="34">34 — בנק ערבי ישראלי</option>
                        <option value="46">46 — בנק מסד</option>
                        <option value="52">52 — בנק פועלי אגודת ישראל</option>
                        <option value="54">54 — בנק ירושלים</option>
                        <option value="67">67 — בנק ערבי לקידום</option>
                        <option value="68">68 — בנק דקסיה ישראל</option>
                        <option value="77">77 — בנק הדואר</option>
                        <option value="99">99 — אחר</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">מספר סניף *</label>
                      <input
                        type="text"
                        placeholder="למשל: 123"
                        value={form.masavBranchCode}
                        onChange={e => setForm({ ...form, masavBranchCode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">מספר חשבון *</label>
                      <input
                        type="text"
                        placeholder="למשל: 123456"
                        value={form.masavAccountNumber}
                        onChange={e => setForm({ ...form, masavAccountNumber: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">שם בעל החשבון</label>
                      <input
                        type="text"
                        placeholder="שם כפי שמופיע בבנק"
                        value={form.masavAccountName}
                        onChange={e => setForm({ ...form, masavAccountName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      {/* רשומת המס״ב מכילה שדה זהות אחד בן 9 ספרות — רק המספר
                          משודר. סוג הזיהוי (ת.ז./ח.פ) הוא שלנו, לתצוגה בלבד. */}
                      <label className="block text-xs text-gray-500 mb-1">מספר זהות</label>
                      <div className="flex gap-2">
                        <select
                          value={form.masavIdType}
                          onChange={e => setForm({ ...form, masavIdType: e.target.value as 'individual' | 'company' })}
                          className="w-24 shrink-0 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                          <option value="individual">ת.ז.</option>
                          <option value="company">ח.פ.</option>
                        </select>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={9}
                          placeholder="9 ספרות"
                          value={form.masavIdNumber}
                          onChange={e => setForm({ ...form, masavIdNumber: e.target.value.replace(/\D/g, '') })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                      </div>
                    </div>
                    {/* סוג החיוב — הרשאה פתוחה (הוראת קבע) מול חיוב בודד.
                        נשלח כ-installmentTotal: 1 לחד־פעמי, N או
                        RECURRING_UNLIMITED להוראת קבע. */}
                    <div className="sm:col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">סוג החיוב</label>
                      <div className="flex flex-col sm:flex-row gap-2 text-sm">
                        {[
                          { v: true, label: '🔄 הוראת קבע' },
                          { v: false, label: '💠 חיוב חד־פעמי' },
                        ].map((opt) => (
                          <label
                            key={String(opt.v)}
                            className={`flex-1 px-3 py-2 rounded-lg border cursor-pointer text-center transition-colors ${
                              form.masavIsRecurring === opt.v
                                ? 'border-indigo-500 bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-indigo-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="masavIsRecurring"
                              checked={form.masavIsRecurring === opt.v}
                              onChange={() => setForm({ ...form, masavIsRecurring: opt.v })}
                              className="sr-only"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    {form.masavIsRecurring ? (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">מספר חודשים</label>
                        <select
                          value={form.masavMonths}
                          onChange={e => setForm({ ...form, masavMonths: Number(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                          {[3, 6, 12, 18, 24, 36].map(m => (
                            <option key={m} value={m}>{m} חודשים</option>
                          ))}
                          <option value={RECURRING_UNLIMITED}>ללא הגבלה</option>
                        </select>
                        {form.masavMonths < RECURRING_UNLIMITED && Number(form.amount) > 0 && (
                          <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                            {form.masavMonths} × ₪{Number(form.amount).toLocaleString('he-IL')} = ₪{(Number(form.amount) * form.masavMonths).toLocaleString('he-IL')} בסך הכל
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="self-end text-xs text-indigo-700 dark:text-indigo-300 leading-relaxed">
                        ההרשאה תחויב פעם אחת בלבד ולאחר מכן תיעצר.
                      </p>
                    )}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">יום חיוב בחודש *</label>
                      <input
                        type="number"
                        min={1}
                        max={28}
                        value={form.masavBillingDay}
                        onChange={e => setForm({ ...form, masavBillingDay: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">ההרשאות מקובצות לפי יום החיוב בעת הפקת הקובץ (1–28).</p>
                    </div>
                    {/* Rendered even for a single institution — at one option it's
                        already preselected, but the choice stays discoverable. */}
                    {masavDebitInstitutions.length >= 1 && (
                      <div className="sm:col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">מוסד מס״ב *</label>
                        <select
                          value={form.masavInstitutionId}
                          onChange={e => setForm({ ...form, masavInstitutionId: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          required
                        >
                          <option value="">בחר מוסד...</option>
                          {masavDebitInstitutions.map(i => (
                            <option key={i._id} value={i._id}>
                              {i.label || i.institutionCode}{i.institutionCode ? ` — ${i.institutionCode}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                <div className="sm:col-span-2">
                  {/* For "אחר" (other) the note doubles as the income-type
                      description, so it's emphasized and prompted. */}
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {form.paymentMethod === 'other' ? '📝 פירוט סוג ההכנסה / הערה' : 'הערות'}
                  </label>
                  <textarea
                    placeholder={form.paymentMethod === 'other' ? 'מה מקור / סוג ההכנסה? (יופיע בדוחות)' : 'הערות פנימיות...'}
                    value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg text-gray-900 dark:text-white resize-none ${
                      form.paymentMethod === 'other'
                        ? 'border-amber-400 ring-1 ring-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                    }`}
                  />
                  {form.paymentMethod === 'other' && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">בחרת "אחר" — מומלץ לפרט את סוג ההכנסה כדי שיהיה ברור בדוחות.</p>
                  )}
                </div>

                {/* מס״ב is excluded on purpose: this branch creates a mandate,
                    not a Donation — nothing has been collected yet, so offering
                    to issue/attach a receipt here would document a payment that
                    never happened. See the note rendered in its place below. */}
                {form.paymentMethod !== 'credit_card' && form.paymentMethod !== 'masav' && (
                  <div className="sm:col-span-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 space-y-3">
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">🧾 קבלה</div>
                    <div className="flex flex-col sm:flex-row gap-2 text-sm">
                      {[
                        { v: 'none' as const, label: 'אל תפיק קבלה' },
                        { v: 'issue' as const, label: 'הפק קבלה דרך מסוף' },
                        { v: 'upload' as const, label: 'העלה קובץ קבלה' },
                      ].map((opt) => (
                        <label
                          key={opt.v}
                          className={`flex-1 px-3 py-2 rounded-lg border cursor-pointer text-center transition-colors ${
                            receiptMode === opt.v
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 font-medium'
                              : 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="receiptMode"
                            value={opt.v}
                            checked={receiptMode === opt.v}
                            onChange={() => setReceiptMode(opt.v)}
                            className="sr-only"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>

                    {receiptMode === 'issue' && (
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">מסוף להפקת הקבלה</label>
                        {terminalOptions.length === 0 ? (
                          <div className="text-xs text-red-600 dark:text-red-400">
                            לא נמצאו מסופי HyPay/Tranzila פעילים. הוסף מסוף בהגדרות.
                          </div>
                        ) : (
                          <select
                            value={receiptTerminalId}
                            onChange={(e) => setReceiptTerminalId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          >
                            {terminalOptions.map((t) => (
                              <option key={t._id} value={t._id}>
                                {t.name} — {t.provider === 'hyp' ? 'HyPay' : t.provider === 'tranzila' ? 'Tranzila' : t.provider}
                                {t.isDefault ? ' (ברירת מחדל)' : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}

                    {receiptMode === 'upload' && (
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            קובץ הקבלה (PDF / JPG / PNG, עד 20MB)
                          </label>
                          <input
                            type="file"
                            accept="application/pdf,image/png,image/jpeg"
                            onChange={(e) => setUploadedReceiptFile(e.target.files?.[0] || null)}
                            className="w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-gray-300 file:bg-white dark:file:bg-gray-700 file:text-sm file:cursor-pointer"
                          />
                          {uploadedReceiptFile && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {uploadedReceiptFile.name} ({Math.round(uploadedReceiptFile.size / 1024)} KB)
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            מספר קבלה (אופציונלי)
                          </label>
                          <input
                            type="text"
                            value={uploadedReceiptNumber}
                            onChange={(e) => setUploadedReceiptNumber(e.target.value)}
                            placeholder="REC-2026-0001"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Stands in for the receipt block above so its absence reads as
                    deliberate rather than a missing control. */}
                {form.paymentMethod === 'masav' && (
                  <div className="sm:col-span-2 p-3 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 space-y-1">
                    <div className="text-sm font-medium text-indigo-800 dark:text-indigo-300">🧾 קבלה</div>
                    <p className="text-xs text-indigo-700 dark:text-indigo-300 leading-relaxed">
                      קבלה תופק לאחר שהחיוב ייגבה בפועל — לאחר שידור הקובץ למס״ב וסיום חלון הסליקה.
                      עד אז לא נרשמה הכנסה, ולכן אין ממה להפיק קבלה.
                    </p>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400">
                      את הקבלה מפיקים מהמסך של קבצי מס״ב.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end mt-6">
                <button type="button" onClick={closeDialog}
                  className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">ביטול</button>
                {form.paymentMethod !== 'credit_card' && (
                  <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{submitting ? 'שומר…' : 'שמור הכנסה'}</button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      <PaymentIframeModal
        url={iframeUrl}
        open={iframeOpen}
        onClose={() => { setIframeOpen(false); setIframeUrl(null); setPendingSessionId(null); }}
        onSuccess={handleIframePaymentSuccess}
        onFail={handleIframePaymentFail}
        title="גביית אשראי מאובטחת"
      />
    </>
  );
}
